import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformContrat } from "@/store/platform/types";
import type { Contrat, ContratInput } from "./types";

interface ContratsContextValue {
  contrats: Contrat[];
  addContrat: (input: ContratInput) => Contrat;
  getContrat: (id: string) => Contrat | undefined;
  removeContrat: (id: string) => void;
  updateContrat: (id: string, input: ContratInput) => void;
  contratsByLocataire: (locataireId: string) => Contrat[];
  contratsByBien: (bienId: string) => Contrat[];
  countByImmeuble: (immeubleId: string) => number;
  countLocatairesByImmeuble: (immeubleId: string) => number;
  stats: { total: number; actifs: number; brouillons: number; expires: number };
}

const ContratsContext = createContext<ContratsContextValue | null>(null);

function toOwnerContrat(source: PlatformContrat): Contrat {
  return {
    id: source.id,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    locataireId: source.locataireId,
    dateDebut: source.dateDebut,
    dateFin: source.dateFin,
    loyerMensuel: source.loyerMensuel,
    caution: source.caution,
    avance: source.avance,
    jourEcheance: source.jourEcheance,
    conditionsParticulieres: source.conditionsParticulieres,
    document: source.document,
    statut: source.statut,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » des contrats du store unifié de la plateforme.
 * Un contrat créé ici (scope « proprietaire ») est le même objet que celui vu
 * par le Super Admin : aucune duplication. L'API exposée reste identique à
 * l'ancien store local, donc les pages n'ont pas bougé.
 */
export function ContratsProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const contrats = useMemo(
    () => platform.contrats.filter((item) => item.scope === "proprietaire").map(toOwnerContrat),
    [platform.contrats]
  );

  const addContrat = useCallback(
    (input: ContratInput) => toOwnerContrat(platform.addContrat({ ...input, scope: "proprietaire", proprietaireId: "" })),
    [platform.addContrat]
  );

  const updateContrat = useCallback(
    (id: string, input: ContratInput) => {
      platform.updateContrat(id, { ...input, scope: "proprietaire", proprietaireId: "" });
    },
    [platform.updateContrat]
  );

  const removeContrat = useCallback(
    (id: string) => {
      platform.removeContrat(id);
    },
    [platform.removeContrat]
  );

  const getContrat = useCallback((id: string) => contrats.find((item) => item.id === id), [contrats]);

  const contratsByLocataire = useCallback(
    (locataireId: string) => contrats.filter((item) => item.locataireId === locataireId),
    [contrats]
  );

  const contratsByBien = useCallback(
    (bienId: string) => contrats.filter((item) => item.bienId === bienId),
    [contrats]
  );

  const countByImmeuble = useCallback(
    (immeubleId: string) => contrats.filter((item) => item.immeubleId === immeubleId).length,
    [contrats]
  );

  const countLocatairesByImmeuble = useCallback(
    (immeubleId: string) =>
      new Set(contrats.filter((item) => item.immeubleId === immeubleId).map((item) => item.locataireId)).size,
    [contrats]
  );

  const stats = useMemo(
    () => ({
      total: contrats.length,
      actifs: contrats.filter((item) => item.statut === "actif").length,
      brouillons: contrats.filter((item) => item.statut === "brouillon").length,
      expires: contrats.filter((item) => item.statut === "expire").length,
    }),
    [contrats]
  );

  const value = useMemo(
    () => ({
      contrats,
      addContrat,
      getContrat,
      removeContrat,
      updateContrat,
      contratsByLocataire,
      contratsByBien,
      countByImmeuble,
      countLocatairesByImmeuble,
      stats,
    }),
    [
      contrats,
      addContrat,
      getContrat,
      removeContrat,
      updateContrat,
      contratsByLocataire,
      contratsByBien,
      countByImmeuble,
      countLocatairesByImmeuble,
      stats,
    ]
  );

  return <ContratsContext.Provider value={value}>{children}</ContratsContext.Provider>;
}

export function useContrats(): ContratsContextValue {
  const context = useContext(ContratsContext);
  if (!context) {
    throw new Error("useContrats doit être utilisé à l'intérieur de ContratsProvider");
  }
  return context;
}